import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════════
// CHAT EMPTY STATE
//
// The hero for a fresh conversation. Centering is handled by the parent
// (`m-auto` on a flex item) rather than a min-height calc, so it stays optically
// centred no matter how tall the composer grows.
// ═══════════════════════════════════════════════════════════════════════════════

export function ChatEmptyState({ quickPrompts = [], onPick, reduceMotion = false }) {
    const rise = (delay) => (reduceMotion
        ? {}
        : {
            initial: { opacity: 0, y: 14 },
            animate: { opacity: 1, y: 0 },
            transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1], delay },
        });

    return (
        <div className="flex flex-col items-center" data-testid="chat-empty-state">
            <motion.div {...rise(0)} className="flex flex-col items-center">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/30 to-accent/20 shadow-lg shadow-primary/10">
                    <Sparkles className="h-7 w-7 text-primary" />
                </div>
                <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-muted-foreground/70">
                    Forge Advisor
                </p>
                <h2 className="mb-2 text-center font-mono text-2xl font-bold tracking-tight md:text-3xl">
                    How can I help you?
                </h2>
                <p className="mb-8 max-w-sm text-center text-sm leading-relaxed text-muted-foreground">
                    Ask anything about your project — brainstorming, code, planning, or docs.
                </p>
            </motion.div>

            <div className="flex w-full flex-col gap-1.5">
                {quickPrompts.map((prompt, i) => (
                    <motion.button
                        key={i}
                        {...rise(0.06 + i * 0.045)}
                        onClick={() => onPick?.(prompt.text)}
                        data-testid={`quick-prompt-${i}`}
                        className="group flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5 text-left transition-colors duration-200 hover:border-primary/30 hover:bg-white/[0.05]"
                    >
                        <span className="w-5 shrink-0 font-mono text-[10px] font-bold tabular-nums tracking-[0.14em] text-muted-foreground/50 transition-colors group-hover:text-primary">
                            {String(i + 1).padStart(2, '0')}
                        </span>
                        <prompt.icon className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                        <span className="flex-1 text-sm text-foreground/80 transition-colors group-hover:text-foreground">
                            {prompt.text}
                        </span>
                        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-primary" />
                    </motion.button>
                ))}
            </div>
        </div>
    );
}

export default ChatEmptyState;
